import { type ReactNode, useState } from 'react';
import { Navigate } from 'react-router';

import { useHasAnyAdminPermission } from '@treneo/features/admin';
import { urls } from '@treneo/shared/constants';
import { useAppTranslations } from '@treneo/shared/i18n';
import { IconSizes, NavigateBeforeIcon } from '@treneo/shared/icons';
import { useAppBack } from '@treneo/shared/router';
import { Colors, Spacing } from '@treneo/shared/themes';
import { Box, IconButton, styled } from '@treneo/shared/ui';

import { AdminNavbar } from './admin-navbar';
import { AdminSidebar } from './admin-sidebar';

const LayoutRoot = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  overflow: 'hidden'
});

const LayoutRow = styled(Box)({
  display: 'flex',
  flex: '1 1 auto',
  width: '100%',
  position: 'relative',
  overflow: 'hidden'
});

const ContentWrapper = styled(Box)({
  flex: '1 1 auto',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  overflow: 'hidden',
  backgroundColor: Colors.lightGrey
});

const AdminBackBar = styled(Box)({
  display: 'flex',
  flexShrink: 0,
  justifyContent: 'flex-end',
  alignItems: 'center',
  padding: Spacing.xsmall,
  paddingRight: Spacing.small
});

const ContentScroll = styled(Box)({
  flex: '1 1 auto',
  overflow: 'auto',
  paddingTop: 0
});

type AdminLayoutProps = {
  children: ReactNode;
};

export const AdminLayout = ({ children }: Readonly<AdminLayoutProps>) => {
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const hasAdminPermission = useHasAnyAdminPermission();
  const back = useAppBack();
  const { t } = useAppTranslations();

  if (!hasAdminPermission) {
    return <Navigate to={urls.homePage} replace />;
  }

  const backButtonAriaLabel = t('common.back');

  return (
    <LayoutRoot>
      <AdminNavbar
        sidebarOpen={isSidebarOpen}
        onToggleSidebar={() => setSidebarOpen(!isSidebarOpen)}
      />
      <LayoutRow>
        <AdminSidebar isOpen={isSidebarOpen} onClose={() => setSidebarOpen(false)} />
        <ContentWrapper>
          <AdminBackBar>
            <IconButton
              aria-label={backButtonAriaLabel}
              onClick={back}
              size='small'
              title={backButtonAriaLabel}
            >
              <NavigateBeforeIcon fontSize={IconSizes.small} />
            </IconButton>
          </AdminBackBar>
          <ContentScroll>{children}</ContentScroll>
        </ContentWrapper>
      </LayoutRow>
    </LayoutRoot>
  );
};
